import {ReactElement} from "react";
import {useNavigate} from "react-router";
import {useSelector} from "react-redux";
import {FuelPurchase, TokenPaymentMethod} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {LoadStates} from "@/components/loadFuel/LoadStates";
import {StripePaymentMethodObject} from "@/types/Stripe";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetFuelBalance, formatFuelUsd} from "@/hooks/useGetFuelBalance";
import {formatPriceAddCommaPreserveDecimals} from "@/utils/currencyFormatters";
import {formatNumberWithCommas} from "@/utils/formatNumberWithCommas";
import {capitalize} from "lodash";
import {FaCheck} from "react-icons/fa6";
import {FaArrowRight} from "react-icons/fa";

interface Props {
    setLoadState: (state: LoadStates) => void;
    fuelPurchase?: FuelPurchase;
    toggleAsModal?: () => void;
    payment: StripePaymentMethodObject;
}

function LoadFuelSuccess(props: Props): ReactElement {
    const tokenName = import.meta.env.VITE_TOKEN_NAME;
    const navigate = useNavigate();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const {data: transactionsData} = useGetFuelBalance(fullToken, currentUser?.user?.id);

    const today = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
    }).format(new Date());

    const isXp = props.fuelPurchase?.paymentMethod === TokenPaymentMethod.XP;

    return (
        <div className="lfs">
            {/* HERO */}
            <section className="lfs-hero">
                <div className="lfs-mark">
                    <FaCheck aria-hidden="true" />
                </div>
                <div className="lfs-eyebrow">Loaded</div>
                <h2 className="lfs-title">{tokenName} Loaded</h2>
                <div className="lfs-amount">
                    {formatPriceAddCommaPreserveDecimals(props.fuelPurchase?.fuel, 0)}
                    <span className="lfs-amount-unit">{tokenName}</span>
                </div>
            </section>

            {/* NEW BALANCE */}
            {transactionsData &&
                <div className="lfs-bal-strip">
                    <span className="lfs-bal-lbl">New Balance</span>
                    <div className="lfs-bal-amounts">
                        <span className="lfs-bal-fiat">{formatFuelUsd(transactionsData.balanceInUsd, {cents: true})}</span>
                        <span className="lfs-bal-fuel">{formatNumberWithCommas(transactionsData.balance)} {tokenName}</span>
                    </div>
                </div>
            }

            {/* RECEIPT */}
            <div className="lfs-receipt">
                <div className="lfs-receipt-row">
                    <span className="label">{isXp ? "Converted" : "Charged"}</span>
                    <strong>
                        {isXp
                            ? `${formatNumberWithCommas(props.fuelPurchase?.xp ?? 0)} XP`
                            : `$${(props.fuelPurchase?.fiat ?? 0).toFixed(2)} USD`}
                    </strong>
                </div>
                {!isXp && props.payment?.card &&
                    <div className="lfs-receipt-row">
                        <span className="label">Card</span>
                        <strong>{capitalize(props.payment.card.brand)} •••• {props.payment.card.last4}</strong>
                    </div>
                }
                <div className="lfs-receipt-row">
                    <span className="label">Date</span>
                    <strong>{today}</strong>
                </div>
            </div>

            {/* CTA — matches anchor: Spend at Shop (primary) + Back to Play (ghost). */}
            <div className="lfs-cta-zone">
                <button
                    type="button"
                    className="lfs-cta-primary"
                    onClick={() => {
                        props.toggleAsModal?.(); navigate("/lootshop");
                    }}
                >
                    Spend at Shop <FaArrowRight aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="lfs-cta-ghost"
                    onClick={() => {
                        props.toggleAsModal?.(); navigate("/play");
                    }}
                >
                    Back to Play
                </button>
            </div>
        </div>
    );
}

export default LoadFuelSuccess;
